<template>
  <BasePopup
    :visible="visible"
    popup-class="bind-role-popup"
    :show-close-btn="true"
    :show-confirm-btn="false"
    :mask-closable="true"
    @update:visible="handleClose"
  >
    <!-- 标题 -->
    <div class="bind-role-popup-title"></div>

    <div class="bind-role-content">
      <!-- 服务器选择 -->
      <div class="bind-row">
        <div class="bind-input-wrapper">
          <div class="bind-label">伺服器</div>
          <div class="bind-divider"></div>
          <select
            v-model="selectedServerSid"
            class="bind-select"
            @change="handleServerChange"
          >
            <option value="" disabled>請選擇伺服器</option>
            <optgroup v-if="recentServers.length > 0" label="最近伺服器">
              <option
                v-for="server in recentServers"
                :key="server.sid"
                :value="server.sid"
              >
                {{ server.sname }}
              </option>
            </optgroup>
            <optgroup label="全部伺服器">
              <option
                v-for="server in allServers"
                :key="server.sid"
                :value="server.sid"
              >
                {{ server.sname }}
              </option>
            </optgroup>
          </select>
          <div class="bind-select-arrow"></div>
        </div>
      </div>

      <!-- 角色选择 -->
      <div class="bind-row">
        <div class="bind-input-wrapper">
          <div class="bind-label">角色名</div>
          <div class="bind-divider"></div>
          <select
            v-model="selectedRoleIndex"
            class="bind-select"
            :disabled="!selectedServerSid || roleList.length === 0"
          >
            <option value="-1" disabled>請選擇角色</option>
            <option
              v-for="(role, index) in roleList"
              :key="role.roleID"
              :value="index"
            >
              {{ role.roleName }}
            </option>
          </select>
          <div class="bind-select-arrow"></div>
        </div>
      </div>

      <!-- 确认按钮 -->
      <div class="bind-confirm-btn" @click="handleConfirm"></div>
    </div>
  </BasePopup>
</template>

<script setup>
import { ref, watch, inject } from "vue";
import { point } from "@/utils";
import BasePopup from "./BasePopup.vue";
import Language from "@/hooks/language.js";
import * as request from "@/api/request";
import * as utils from "@/utils";
import { useDebounceLock } from "@/hooks/useDebounce";

const { langData } = Language();

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  platformId: {
    type: String,
    default: "global",
  },
  activityName: {
    type: String,
    required: true,
  },
  gameId: {
    type: Number,
    required: true,
  },
  allowNewServerIds: {
    type: Array,
    default: () => [],
  },
  // 当前绑定信息（用于切换角色时回显）
  currentBindInfo: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:visible", "bind-success", "bind-failed"]);

// 注入全局方法
const showTips = inject("showTips", (msg) => {
  alert(msg);
});
const showCurErrorTips = inject("showCurErrorTips", (code) => {
  alert(`错误码: ${code}`);
});

// 状态
const recentServers = ref([]); // 最近服务器
const allServers = ref([]); // 全部服务器
const roleList = ref([]);
const selectedServerSid = ref("");
const selectedRoleIndex = ref(-1);

// 初始化服务器列表
const initServerList = async () => {
  const res = await request.getJSON(
    utils.getActivityApi(props.platformId, props.activityName, "listServerNew"),
    {
      params: JSON.stringify({
        gameId: props.gameId,
        server_limit_recent: 10,
      }),
    }
  );

  if (res?.result === 1) {
    // 获取最近服务器和全部服务器
    const recent = res.data?.recent_servers || [];
    const all = res.data?.all_servers || [];

    // 筛选允许的新服
    if (props.allowNewServerIds.length > 0) {
      recentServers.value = recent.filter((item) =>
        props.allowNewServerIds.includes(item.sid)
      );
      allServers.value = all.filter((item) =>
        props.allowNewServerIds.includes(item.sid)
      );
    } else {
      recentServers.value = recent;
      allServers.value = all;
    }
  } else {
    // 使用统一的错误提示函数
    showCurErrorTips(res?.code);
  }
};

// 初始化角色列表
const initRoleList = async (serverId) => {
  roleList.value = [];
  const res = await request.getJSON(
    utils.getActivityApi(
      props.platformId,
      props.activityName,
      "getMobileGameRole"
    ),
    {
      params: JSON.stringify({
        server_id: serverId,
      }),
    }
  );

  if (res?.result === 1) {
    roleList.value = res.data || [];
  } else {
    showTips(
      langData.noNewServerRoleTips || "未查詢到角色，請先前往遊戲創建角色"
    );
  }
};

// 服务器切换
const handleServerChange = async () => {
  selectedRoleIndex.value = -1;
  roleList.value = [];

  if (selectedServerSid.value) {
    await initRoleList(selectedServerSid.value);
  }
};

// 绑定角色
const bindRole = useDebounceLock(async () => {
  if (!selectedServerSid.value || selectedRoleIndex.value < 0) {
    showTips("請選擇服務器和角色");
    return;
  }

  // 从所有服务器列表中查找（包括最近和全部）
  const allServerList = [...recentServers.value, ...allServers.value];
  const selectedServer = allServerList.find(
    (item) => item.sid === selectedServerSid.value
  );
  const selectedRole = roleList.value[selectedRoleIndex.value];

  if (!selectedServer || !selectedRole) {
    return;
  }

  const res = await request.getJSON(
    utils.getActivityApi(props.platformId, props.activityName, "bind"),
    {
      params: JSON.stringify({
        roleId: selectedRole.roleID,
        serverId: selectedServer.sid,
        sId: selectedServer.id,
        serverName: selectedServer.sname,
        roleName: encodeURIComponent(encodeURIComponent(selectedRole.roleName)),
      }),
    }
  );

  if (res?.result === 1) {
    emit("bind-success", {
      serverId: selectedServer.sid,
      serverName: selectedServer.sname,
      sid: selectedServer.id,
      roleId: selectedRole.roleID,
      roleName: selectedRole.roleName,
    });
    handleClose();
  } else {
    emit("bind-failed", res?.code);
    // 使用统一的错误提示函数
    showCurErrorTips(res?.code);
  }
});

// 确认按钮
const handleConfirm = () => {
  bindRole();
};

// 关闭弹窗
const handleClose = () => {
  emit("update:visible", false);
};

// 监听弹窗显示状态
watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      // 打点上报：绑定角色弹窗展示
      point({
        eventName: "show_popup",
        eventType: "绑定角色弹窗",
      });
      // 弹窗打开时，初始化数据
      selectedServerSid.value = "";
      selectedRoleIndex.value = -1;
      roleList.value = [];
      recentServers.value = [];
      allServers.value = [];

      // 先加载服务器列表
      await initServerList();

      // 如果有当前绑定信息，自动选中对应的服务器和角色
      if (props.currentBindInfo) {
        const { serverId, sId, roleId, roleName } = props.currentBindInfo;

        // 选中服务器（使用 serverId，它对应 select 的 v-model）
        if (serverId) {
          selectedServerSid.value = String(serverId);

          // 加载该服务器的角色列表（使用 serverId）
          await initRoleList(serverId);

          // 选中角色
          if (roleId && roleList.value.length > 0) {
            const roleIndex = roleList.value.findIndex(
              (role) => String(role.roleID) === String(roleId)
            );
            if (roleIndex !== -1) {
              selectedRoleIndex.value = roleIndex;
            }
          }
        }
      }
    }
  }
);
</script>

<style lang="less" scoped>
/* 样式已在 @/assets/less/popup.less 中定义 */
</style>
